import React, { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { AiReviewConfigFormModal } from 'src/components/ai-review/config-form/AiReviewConfigFormModal'
import { AiReviewConfigsTable } from 'src/components/ai-review/configs-table/AiReviewConfigsTable'
import { AiReviewConfigsTableToolbar } from 'src/components/ai-review/configs-table/AiReviewConfigsTableToolbar'
import { DeleteAiReviewConfigModal } from 'src/components/ai-review/delete/DeleteAiReviewConfigModal'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import {
  AdminCreateAiDocumentReviewConfig,
  AdminDeleteAiDocumentReviewConfig,
  AdminFetchAiDocumentReviewConfigs,
  AdminUpdateAiDocumentReviewConfig,
} from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  AiDocumentReviewConfig,
  AiDocumentReviewConfigFormValues,
} from 'src/interfaces/aiDocumentReviewConfig'
import { getAiDocumentReviewConfigsError } from 'src/selectors/aiDocumentReviewConfig'
import { buildCreatePayload, buildUpdatePayload } from 'src/utils/aiReviewConfigPayload'

interface FormState {
  config: AiDocumentReviewConfig | null
}

const CustomerAiReviewPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { isAuthenticated } = useAuthSession()

  const [refreshToken, setRefreshToken] = useState<number>(0)
  const [formState, setFormState] = useState<FormState | null>(null)
  const [deletedConfig, setDeletedConfig] = useState<AiDocumentReviewConfig | null>(null)

  const error = useSelector(getAiDocumentReviewConfigsError)
  // Add, Edit and Delete all call `ai_document_review_configs_manage` endpoints.
  const canManage = useHasPermission(InternalPermission.AiDocumentReviewConfigsManage)

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // Held until the internal session is confirmed, so no request goes out without a token to
  // carry — the standalone page this replaced fetched on `customerId` alone and could 401 on a
  // cold load. The endpoint takes no search or pagination params, so this only reruns on a
  // customer change, a session change, or a manual refresh.
  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      return
    }

    asyncDispatchWithErrorToast(
      AdminFetchAiDocumentReviewConfigs.request({ partnerId: customerId }),
    )
  }, [asyncDispatchWithErrorToast, isAuthenticated, customerId, refreshToken])

  // The submit handlers await their dispatches and this component survives a customer-only
  // navigation, so a request issued for customer A can resolve after the route moved to B.
  // Each continuation compares its captured id against this live ref before applying anything.
  const currentCustomerIdRef = useRef<string | undefined>(customerId)

  // The tab route is keyed by slug, not by customerId, so this page stays mounted when only the
  // customer changes. Both dialogs are bound to one customer's row — left open they would show
  // A's configuration under B's header, and saving would dispatch A's id past the guard below,
  // which only runs after the request has already gone out.
  useEffect(() => {
    currentCustomerIdRef.current = customerId
    setFormState(null)
    setDeletedConfig(null)
  }, [customerId])

  const handleOpenCreate = useCallback(() => {
    setFormState({ config: null })
  }, [])

  const handleOpenEdit = useCallback((config: AiDocumentReviewConfig) => {
    setFormState({ config })
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormState(null)
  }, [])

  const handleOpenDelete = useCallback((config: AiDocumentReviewConfig) => {
    setDeletedConfig(config)
  }, [])

  const handleCloseDelete = useCallback(() => {
    setDeletedConfig(null)
  }, [])

  const handleSubmit = useCallback(
    async (values: AiDocumentReviewConfigFormValues) => {
      if (!formState || !customerId) {
        return
      }

      const editedConfig = formState.config
      const request = editedConfig
        ? AdminUpdateAiDocumentReviewConfig.request({
            aiDocumentReviewConfigId: editedConfig.id,
            values: buildUpdatePayload(values),
          })
        : AdminCreateAiDocumentReviewConfig.request(buildCreatePayload(customerId, values))

      const result = await asyncDispatchWithErrorToast<unknown>(request)

      if (!result) {
        return
      }

      if (currentCustomerIdRef.current !== customerId) {
        return
      }

      snackbar?.enqueueSnackbar(editedConfig ? 'Configuration updated' : 'Configuration created', {
        variant: 'success',
      })
      setFormState(null)
      handleRefresh()
    },
    [formState, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deletedConfig || !customerId) {
      return
    }

    const result = await asyncDispatchWithErrorToast<unknown>(
      AdminDeleteAiDocumentReviewConfig.request({ aiDocumentReviewConfigId: deletedConfig.id }),
    )

    if (!result) {
      return
    }

    if (currentCustomerIdRef.current !== customerId) {
      return
    }

    snackbar?.enqueueSnackbar('Configuration deleted', { variant: 'success' })
    setDeletedConfig(null)
    handleRefresh()
  }, [deletedConfig, customerId, asyncDispatchWithErrorToast, snackbar, handleRefresh])

  return (
    <Fragment>
      <div data-testid="admin-customer-ai-review-page" css={tw`min-w-0 flex-1 p-6`}>
        <div css={tw`mb-5 flex flex-row items-start justify-between gap-4`}>
          <div css={tw`min-w-0`}>
            <Typography variant="h2">AI document review</Typography>
            <Typography variant="body2" css={tw`mt-1 text-shades-500`}>
              Every document of this customer is reviewed in shadow mode by default. These rows
              refine that one document at a time.
            </Typography>
          </div>
          {canManage ? (
            <Button
              size="small"
              color="dark"
              className={tw`px-4`}
              leftIcon={<GSIcon iconName="gs_plus_medium" size={20} css={tw`text-shades-0`} />}
              onClick={handleOpenCreate}
              data-testid="ai-review-configs-add-button"
            >
              Add configuration
            </Button>
          ) : null}
        </div>

        {error ? (
          <Typography
            variant="body2"
            css={tw`mb-4 rounded-lg bg-critical-50 p-3 text-critical-500`}
            data-testid="ai-review-configs-error"
          >
            {error}
          </Typography>
        ) : null}

        <AiReviewConfigsTableToolbar onRefresh={handleRefresh} />
        <AiReviewConfigsTable
          canManage={canManage}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
        />
      </div>

      <AiReviewConfigFormModal
        isOpen={formState !== null}
        config={formState?.config ?? null}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />
      <DeleteAiReviewConfigModal
        config={deletedConfig}
        onCancel={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />
    </Fragment>
  )
}

export const CustomerAiReviewPage = memo(CustomerAiReviewPageComponent)
